import { CustomEase } from "@lib/gsap";

/**
 * Easing curves extracted from osmo.supply/easings.
 *
 * Each entry is a cubic-bezier control-point tuple `[x1, y1, x2, y2]`
 * (same format CSS `cubic-bezier()` and GSAP's CustomEase shorthand use).
 *
 * Two helpers are exposed:
 *  - `easing(token)`   → CSS `cubic-bezier(...)` string
 *  - `gsapEase(token)` → string name for `gsap.to(el, { ease: gsapEase("osmo") })`
 *                         (lazily registers a CustomEase under the token name)
 *
 * Multi-segment curves (Bounce, Elastic, Material Emphasized) are not
 * included — use GSAP's native names for those: `"bounce.out"`, `"elastic.out"`, etc.
 */

export const EASING_BEZIER = {
  // Osmo favorites
  osmo: [0.625, 0.05, 0, 1],
  energy: [0.32, 0.72, 0, 1],
  smooth: [0.38, 0.005, 0.215, 1],
  punch: [0.19, 1, 0.22, 1],
  relaxed: [0.7, 0, 0.3, 1],
  expoInOut: [0.87, 0, 0.13, 1],
  jump: [0.35, 1.5, 0.6, 1],
  pop: [0.17, 0.67, 0.3, 1.33],
  anticipate: [0.8, -0.4, 0.5, 1],
  fade: [0.25, 0.1, 0.25, 1],

  // Figma — back overshoots + soft curves
  backIn: [0.3, -0.05, 0.7, -0.5],
  backOut: [0.45, 1.45, 0.8, 1],
  backInOut: [0.7, -0.4, 0.4, 1.4],
  gentle: [0.2, 1.3, 0.6, 1],
  slow: [0.17, 0.55, 0.15, 1],

  // Material — system motion
  materialStandard: [0.2, 0, 0, 1],
  materialDecelerate: [0, 0, 0, 1],
  materialAccelerate: [0.3, 0, 1, 1],
  emphasizedDecelerate: [0.05, 0.7, 0.1, 1],
  emphasizedAccelerate: [0.3, 0, 0.8, 0.15],

  // Jitter
  natural: [0.8, 0, 0.2, 1],
} as const;

export type EasingToken = keyof typeof EASING_BEZIER;

export const EASINGS = Object.fromEntries(
  Object.entries(EASING_BEZIER).map(([k, v]) => [
    k,
    `cubic-bezier(${(v as readonly number[]).join(", ")})`,
  ]),
) as Record<EasingToken, string>;

/** CSS `cubic-bezier(...)` string for the token. */
export function easing(token: EasingToken): string {
  return EASINGS[token];
}

const registered = new Set<EasingToken>();

/**
 * Lazily register a `CustomEase` for the token and return its name. Pass
 * directly to GSAP:
 *
 *   gsap.to(el, { ease: gsapEase("osmo"), duration: 0.8 });
 */
export function gsapEase(token: EasingToken): string {
  if (!registered.has(token)) {
    const [x1, y1, x2, y2] = EASING_BEZIER[token];
    CustomEase.create(token, `${x1},${y1},${x2},${y2}`);
    registered.add(token);
  }
  return token;
}
